import React, { useMemo } from 'react';
import { SurchargeConfigForm, type SurchargeSectionConfig, type SurchargeSummaryToken } from '@/components/config_forms/surcharge/SurchargeConfigForm';
import { HolidayConfig } from './UserHolidayConfig';
import { MealBreakConfig } from './UserMealBreakConfig';
import { ShortTurnConfig } from '../user_short_turn/UserShortTurnConfig';
import { UserOvernightConfig } from './UserOvernightConfig';
import { useUserConfigState, useUserConfigDispatch } from '@/context/UserConfigContext';
import { Clock, Calendar, Utensils, Moon } from 'lucide-react';
import { handleUsesShortTurnToggle } from '../user_short_turn/shortTurnHelpers';

// Summary badge labels — mirrors buttonLabel ?? label from each surcharge's method definitions
// Source: short_turn/utils/surchargeMethods.ts, meal_break/sections/MealSurchargeMethodSelector.tsx,
//         holiday/components/HolidayPremiumMethodSelector.tsx, overnight/components/OvernightPremiumMethodSelector.tsx
const SHORT_TURN_METHOD_LABELS: Record<string, string> = {
  carry_over: 'Carry Over',
  bump_up: 'Bump Up',
  fixed_offset: 'Offset Overtime',
  fixed_dollar: 'Fixed Premium',
  hourly_premium: 'Hourly Premium',
  multiplier_floor: 'Guaranteed Rate',
};

const MEAL_METHOD_LABELS: Record<string, string> = {
  offset_overtime: 'Offset Overtime',
  credit_extra_time: 'Credit Time',
  fixed_fee: 'Fixed Fee',
  hourly_premium: 'Hourly Premium',
  guaranteed_rate: 'Guaranteed Rate',
};

const HOLIDAY_METHOD_LABELS: Record<string, string> = {
  starting_multiplier_offset: 'Offset Overtime',
  fixed_dollar: 'Fixed Premium',
  hourly_premium: 'Hourly Premium',
  multiplier_floor: 'Guaranteed Rate',
};

const OVERNIGHT_METHOD_LABELS: Record<string, string> = {
  offset_overtime: 'Offset Overtime',
  hourly_premium: 'Hourly Premium',
  flat_rate: 'Flat Rate',
  credit_extra_time: 'Credit Time',
  guaranteed_rate: 'Guaranteed Rate',
};

function buildTokens(...items: (SurchargeSummaryToken | null)[]): SurchargeSummaryToken[] | undefined {
  const filtered = items.filter((t): t is SurchargeSummaryToken => t !== null);
  return filtered.length > 0 ? filtered : undefined;
}

interface UserSurchargeConfigProps {
  noCard?: boolean;
}

export const UserSurchargeConfig: React.FC<UserSurchargeConfigProps> = ({ noCard }) => {
  const state = useUserConfigState();
  const { updateBillingConfig } = useUserConfigDispatch();

  const commonProps = useMemo(() => ({
    roles: state.roles,
    rateMultipliers: state.rateMultipliers,
    minimumBillableHours: state.billingStructureConfig?.minimumBillableHours
  }), [state.roles, state.rateMultipliers, state.billingStructureConfig?.minimumBillableHours]);

  // Check if any surcharge rules are enabled
  const hasActiveSurcharges = state.usesShortTurn || state.applyHolidayPremium || state.mealConfig?.applyMealSurcharge || state.overnightConfig?.applyOvernightPremium;

  // Build summary tokens for each surcharge type
  const shortTurnTokens = useMemo(() => {
    if (!state.usesShortTurn || !state.shortTurnConfig) return undefined;
    return buildTokens(
      state.shortTurnConfig.requiredBreakHours != null
        ? { label: 'Rest', value: `${state.shortTurnConfig.requiredBreakHours}h` }
        : null,
      state.shortTurnConfig.surchargeType
        ? { label: 'Method', value: SHORT_TURN_METHOD_LABELS[state.shortTurnConfig.surchargeType] ?? state.shortTurnConfig.surchargeType }
        : null,
    );
  }, [state.usesShortTurn, state.shortTurnConfig]);

  const mealTokens = useMemo(() => {
    if (!state.mealConfig?.applyMealSurcharge) return undefined;
    return buildTokens(
      state.mealConfig.mealDeadlineHours != null
        ? { label: 'Deadline', value: `${state.mealConfig.mealDeadlineHours}h` }
        : null,
      state.mealConfig.mealSurchargeType
        ? { label: 'Method', value: MEAL_METHOD_LABELS[state.mealConfig.mealSurchargeType] ?? state.mealConfig.mealSurchargeType }
        : null,
    );
  }, [state.mealConfig]);

  const holidayTokens = useMemo(() => {
    if (!state.applyHolidayPremium) return undefined;
    return buildTokens(
      (state.holidayCatalogIds?.length ?? 0) + (state.holidayCustomIds?.length ?? 0) > 0
        ? { label: 'Holidays', value: `${(state.holidayCatalogIds?.length ?? 0) + (state.holidayCustomIds?.length ?? 0)}` }
        : null,
      state.holidayPremiumType
        ? { label: 'Method', value: HOLIDAY_METHOD_LABELS[state.holidayPremiumType] ?? state.holidayPremiumType }
        : null,
    );
  }, [state.applyHolidayPremium, state.holidayCatalogIds, state.holidayCustomIds, state.holidayPremiumType]);

  const overnightTokens = useMemo(() => {
    if (!state.overnightConfig?.applyOvernightPremium) return undefined;
    return buildTokens(
      state.overnightConfig.startTime && state.overnightConfig.endTime
        ? { label: 'Window', value: `${state.overnightConfig.startTime}–${state.overnightConfig.endTime}` }
        : null,
      state.overnightConfig.premiumType
        ? { label: 'Method', value: OVERNIGHT_METHOD_LABELS[state.overnightConfig.premiumType] ?? state.overnightConfig.premiumType }
        : null,
    );
  }, [state.overnightConfig]);

  const sections: SurchargeSectionConfig[] = [
    {
      key: 'shortTurn',
      title: 'Short Turn Surcharge',
      icon: Clock,
      component: ShortTurnConfig,
      isCustomized: state.usesShortTurn,
      description: "Charge extra when rest between consecutive work days falls below your minimum.",
      isChecked: state.usesShortTurn,
      onCheckedChange: (checked) => handleUsesShortTurnToggle(state.shortTurnConfig, state.rateMultipliers, updateBillingConfig, checked),
      summaryTokens: shortTurnTokens,
    },
    {
      key: 'mealBreak',
      title: 'Meal Break Surcharge',
      icon: Utensils,
      component: MealBreakConfig,
      isCustomized: state.mealConfig.applyMealSurcharge,
      description: "Enforce surcharges for missed or late meal breaks.",
      isChecked: state.mealConfig?.applyMealSurcharge || false,
      onCheckedChange: (checked) => updateBillingConfig({ mealConfig: { ...state.mealConfig, applyMealSurcharge: checked } }),
      summaryTokens: mealTokens,
    },
    {
      key: 'holiday',
      title: 'Holiday Premiums',
      icon: Calendar,
      component: HolidayConfig,
      isCustomized: state.applyHolidayPremium,
      description: "Enable special rates for work performed on selected holidays.",
      isChecked: state.applyHolidayPremium,
      onCheckedChange: (checked) => updateBillingConfig({ applyHolidayPremium: checked }),
      summaryTokens: holidayTokens,
    },
    {
      key: 'overnight',
      title: 'Overnight Premium',
      icon: Moon,
      component: UserOvernightConfig,
      isCustomized: state.overnightConfig.applyOvernightPremium,
      description: "Apply premium compensation for hours worked during a late-night window.",
      isChecked: state.overnightConfig?.applyOvernightPremium || false,
      onCheckedChange: (checked) => updateBillingConfig({ overnightConfig: { ...state.overnightConfig, applyOvernightPremium: checked } }),
      summaryTokens: overnightTokens,
    },
  ];

  return (
    <div className="w-full">
      <SurchargeConfigForm
        title="Default Surcharge Rules"
        description="Define your standard surcharges for holidays, meal breaks, and short turns."
        sections={sections}
        hasAnyCustomization={hasActiveSurcharges}
        commonProps={commonProps}
        variant="user"
        noCard={noCard}
      />
    </div>
  );
};
